import { useRef, useState, useEffect, createContext, useContext } from "react";
import { useDateFilterContext } from "../providers/DateFilterProvider.tsx";
import { FilterCheckboxList } from "../../app-instantsearch/filter/FilterCheckboxList";
import { ResetFilters } from "../../app-instantsearch/components/ResetFilters.tsx";
import { useCurrentRefinements } from "react-instantsearch";
import { FilterDateRange } from "../../app-instantsearch/filter/FilterDateRange";
import { useTranslations } from "../../hooks/useTranslations.ts";
import {useConfigContext} from "../../ConfigProvider.tsx";

const FilterDropdownContext = createContext<{
    openDropdown: string | null;
    setOpenDropdown: (attribute: string | null) => void;
}>({
    openDropdown: null,
    setOpenDropdown: () => {}
});

type FilterDropdownProps = {
    label: string;
    attribute: string;
};

function formatRange(start: Date | null, end: Date | null): string {
    if (!start || !end) return "Zeitraum";
    return `${start.toLocaleDateString()} – ${end.toLocaleDateString()}`;
}

function FilterDropdown({ label, attribute }: FilterDropdownProps) {
    const { items } = useCurrentRefinements();
    const count = items.find((item) => item.attribute === attribute)?.refinements.length || 0;
    const { openDropdown, setOpenDropdown } = useContext(FilterDropdownContext);
    const isOpen = openDropdown === attribute;

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        setOpenDropdown(isOpen ? null : attribute);
    };

    return (
        <div className="ais-filter-dropdown" data-attribute={attribute}>
            <button
                className="ais-filter-dropdown__summary"
                onClick={handleClick}
                aria-expanded={isOpen}
                type="button"
            >
                {label}
                {count > 0 && <span className="ais-filter-dropdown__badge">{count}</span>}
            </button>
            <div className="ais-filter-dropdown__content" style={{ display: isOpen ? 'block' : 'none' }}>
                <FilterCheckboxList attribute={attribute} title="" />
            </div>
        </div>
    );
}

function FilterBarContent() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [openDateFilter, setOpenDateFilter] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const { items } = useCurrentRefinements();
  const { startDate, endDate, setRange, reset } = useDateFilterContext();
  const dateCount = startDate && endDate ? 1 : 0;
  const totalCount =
    items.reduce((sum, item) => sum + item.refinements.length, 0) + dateCount;

  const tc = useTranslations();
    const {
        algoliaConfig: { isSearch },
      } = useConfigContext();

  const { setOpenDropdown } = useContext(FilterDropdownContext);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Element;
            if (!target) return;

            // Check if clicked inside any dropdown content (the part that should stay open)
            const clickedInDropdownContent = target.closest('.ais-filter-dropdown__content');

            // Check if clicked on a dropdown button/summary (should toggle, not close)
            const clickedOnDropdownButton = target.closest('.ais-filter-dropdown__summary');

            // Check if clicked inside any date picker (might render outside)
            const clickedInDatePicker = target.closest('.ais-filterDateRange__picker');



            // Close dropdowns if:
            // - NOT clicked in dropdown content AND
            // - NOT clicked on dropdown button AND
            // - NOT clicked in date picker AND
            // - Either completely outside container OR inside container but outside any dropdown parts
            const shouldClose = !clickedInDropdownContent &&
                              !clickedOnDropdownButton &&
                              !clickedInDatePicker;

            if (shouldClose) {
                setOpenDropdown(null);
                setOpenDateFilter(false);
                setIsMobileOpen(false);
            }
        };

        if (typeof document !== "undefined") {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [setOpenDropdown]);

    const handleDateSelected = (start: Date, end: Date) => {
        setRange(start, end);
    };

    const closeDateDropdown = () => setOpenDateFilter(false);

    return (
        <>
            <div className="ais-filter-toggle-mobile">
                <button onClick={() => setIsMobileOpen(true)}>
                    {tc.filter}
                    {totalCount > 0 && (
                        <span className="ais-filter-dropdown__badge">{totalCount}</span>
                    )}
                </button>
            </div>
            <div className={`ais-mobile-filter-overlay ${isMobileOpen ? 'mobile-open' : ''}`}>
            </div>
            <div ref={containerRef} className={`ais-filters-inline ${isMobileOpen ? 'mobile-open' : ''}`}>
                <div className="ais-filters-header-mobile">
                    <button onClick={() => setIsMobileOpen(false)} aria-label="Filter schließen">
                        <i className="iconNew iconNew-close" />
                    </button>
                </div>

                <FilterDropdown label="Ort" attribute="attributes.Ort" />

                <div className="ais-filter-dropdown" data-attribute="attributes.Zeitraum">
                    <button
                        className="ais-filter-dropdown__summary"
                        onClick={(e) => { e.preventDefault(); setOpenDateFilter(!openDateFilter); setOpenDropdown(null); }}
                        aria-expanded={openDateFilter}
                        type="button"
                    >
                        {formatRange(startDate, endDate)}
                        {startDate && endDate && (
                            <button type="button" className="ais-button-reset" onClick={(e) => { e.stopPropagation(); reset(); closeDateDropdown(); }}>
                                <i className="iconNew iconNew-trash"></i>
                            </button>
                        )}
                    </button>

                    {openDateFilter && (
                        <div className="ais-filter-dropdown__content">
                            <FilterDateRange
                                onRangeSelected={handleDateSelected}
                                onClose={closeDateDropdown}
                            />
                        </div>
                    )}
                </div>

                <FilterDropdown label="Zielgruppe" attribute="attributes.Zielgruppe" />
                <FilterDropdown label="Veranstaltungsart" attribute="attributes.Veranstaltungsart" />
                <FilterDropdown label="Lernformat" attribute="attributes.Lernformat" />
                <FilterDropdown label="Kursleiter" attribute="courseLeaders.name" />
                {isSearch && (
                    <FilterDropdown label="Thema" attribute="attributes.Thema" />
                )}
                <div className="ais-filters-inline__reset">
                    <ResetFilters />
                </div>
            </div>
        </>
    );
}

export function FilterBar() {
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);

    return (
        <FilterDropdownContext.Provider value={{ openDropdown, setOpenDropdown }}>
            <FilterBarContent />
        </FilterDropdownContext.Provider>
    );
}
